// src/pages/Register.jsx
//
// Registration is handled by the Cognito Hosted UI.
// This page redirects the user to Cognito's /signup endpoint so they can
// create an account with email + password. After confirming their email,
// Cognito redirects back to the app and the OIDC flow completes automatically.
//
// Why redirect instead of a custom form?
//   - The app uses react-oidc-context (OIDC Authorization Code flow).
//   - There is no custom /api/register backend route — Cognito IS the identity provider.
//   - Cognito's hosted UI handles password policy enforcement, email verification,
//     and the PostConfirmation Lambda trigger (which creates the DynamoDB user profile).

import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (isLoggedIn) {
      // Already logged in — send to home
      window.location.href = '/';
      return;
    }

    // Build the Cognito Hosted UI signup URL.
    // Using the same env vars that AuthContext uses for login.
    const cognitoDomain = process.env.REACT_APP_COGNITO_AUTH_URL;
    const clientId      = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri   = process.env.REACT_APP_REDIRECT_URI;

    if (!cognitoDomain || !clientId || !redirectUri) {
      console.error('[Register] Missing Cognito env vars — cannot redirect to signup');
      return;
    }

    const params = new URLSearchParams({
      client_id:     clientId,
      response_type: 'code',
      scope:         'openid email profile',
      redirect_uri:  redirectUri,
    });

    // Use /oauth2/authorize with the signup view so that after the user
    // creates their account AND verifies their email, Cognito completes a
    // full authorization-code flow and redirects back with ?code=... —
    // which react-oidc-context processes automatically, logging the user in.
    // (Using /signup redirects back without a code, leaving the user logged out.)
    window.location.href = `${cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }, [isLoggedIn]);

  // Brief loading state while the redirect happens
  return (
    <div className="flex justify-center items-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Redirecting to registration…</p>
      </div>
    </div>
  );
}
